import React, { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../contexts";
import { SettingsContext } from "../../contexts";

// ─── Tab icons ────────────────────────────────────────────────────────────────

const IClients = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);

const IMerchants = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l1.5-6h15L21 9"/>
    <path d="M3 9h18v1a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0V9z"/>
    <path d="M5 10v9a1 1 0 001 1h12a1 1 0 001-1v-9"/>
    <line x1="9" y1="15" x2="15" y2="15"/>
  </svg>
);

const IExperts = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const TAB_ICONS = {
  "/users/client":   <IClients />,
  "/users/merchant": <IMerchants />,
  "/users/expert":   <IExperts />,
};

// ─── Tab definitions ──────────────────────────────────────────────────────────

// CORRECT use of `is_dao_admin` (= `is_admin && !is_merchant`): the
// staff-vs-merchant distinction. Both lists are store-scoped on the backend —
// UserSearchService::searchMerchants() and ::getAllExperts() both apply
// applyStoreIdMConstraint(), so a tenant store admin only ever sees their own
// store's merchant/expert users. There is no cross-tenant read here, so these
// must NOT be tightened to is_master_admin: that would remove a working,
// correctly-scoped directory from every tenant. A merchant user must not get the
// tabs (they'd be browsing their peers), which is exactly what the flag encodes.
// Keep in step with merchants/pages/MerchantsPage.jsx + ExpertsPage.jsx, which
// guard the routes these tabs link to with the same predicate.
const getTabs = (user) => [
  { label: "Clients",   link: "/users/client" },
  ...(user.is_dao_admin ? [{ label: "Merchants", link: "/users/merchant" }] : []),
  ...(user.is_dao_admin ? [{ label: "Experts",   link: "/users/expert"   }] : []),
];

// ─── Component ────────────────────────────────────────────────────────────────

export function UsersTabs() {
  const { user } = useContext(UserContext);
  const settings = useContext(SettingsContext);
  const { pathname } = useLocation();
  const tabs = useMemo(() => getTabs(user), [user]);

  return (
    <>
      {tabs.map((tab) => {
        const isActive = pathname.startsWith(tab.link);
        return (
          <Link
            key={tab.label}
            to={tab.link + `?${settings.iframeQueryParams}`}
            className={`users-tab-bar__tab${isActive ? " users-tab-bar__tab--active" : ""}`}
          >
            <span className="users-tab-bar__icon">
              {TAB_ICONS[tab.link]}
            </span>
            <span className="users-tab-bar__label">{tab.label}</span>
          </Link>
        );
      })}
    </>
  );
}

/**
 * The same tabs as `<UsersTabs />`, shaped for the shared <SubTopbar> / <PageHeader>
 * `tabs` prop so the Users page gets the standard tab row.
 */
export function useUsersTabs() {
  const { user } = useContext(UserContext);
  const settings = useContext(SettingsContext);

  return useMemo(
    () =>
      getTabs(user).map((tab) => ({
        label: tab.label,
        to: `${tab.link}?${settings.iframeQueryParams}`,
        icon: TAB_ICONS[tab.link],
        match: (pathname) => pathname.startsWith(tab.link),
      })),
    [user, settings.iframeQueryParams]
  );
}
